import { createSyncStore } from '../factory';

type UpdateAvailable = { version: string; urgency: 'low' | 'medium' | 'high' } | null;

type AppInfoState = {
  version: string | undefined;
  updateAvailable: UpdateAvailable;
};

type AppInfoActions = {
  setVersion: (version: string) => void;
  setUpdateAvailable: (update: UpdateAvailable) => void;
};

/**
 * Synced store for app-level info that the shell (Electron main process)
 * pushes to all renderer windows.
 *
 * The shell sets `version` via `shellBridge.setState('app-info', { version })`.
 * The shell sets `updateAvailable` via `shellBridge.setState('app-info', { updateAvailable })`.
 * Renderers read reactively: `useAppInfoStore(s => s.version)` or `useAppInfoStore(s => s.updateAvailable)`.
 *
 * Channel: `app-info`
 */
export const useAppInfoStore = createSyncStore<AppInfoState, AppInfoActions>({
  channel: 'app-info',
  syncKeys: ['version', 'updateAvailable'],
  throttleMs: 50,
  syncOptions: { broadcastInitialState: false },

  state: {
    version: undefined,
    updateAvailable: null,
  },

  actions: (set) => ({
    setVersion: (version: string) => set({ version }),
    setUpdateAvailable: (update: UpdateAvailable) => set({ updateAvailable: update }),
  }),
});
